import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SidenavService } from 'src/app/sidenav/sidenav.service';
import { OverviewComponent } from './overview/overview.component';
import { Subscription } from 'rxjs';
import { LocalStorageService } from '../core/services/local-storage.service';
import { PageTitleService } from '../services/pagetitle.service';
import { UxComponent } from './ux/ux.component';
import { WebsiteAuditService } from './website-audit.service';
import { NotificationService } from '../core/services/notification.service';
import { ManagementComponent } from './management/management.component';
import { CrComponent } from './cr/cr.component';
import { DiagnosticComponent } from './diagnostic/diagnostic.component';
import { TechnicalComponent } from './technical/technical.component';
import { SeoComponent } from './seo/seo.component';
import { GeoComponent } from './geo/geo.component';
import { SeoService } from '../seo/seo.service';
import { SelectionService } from '../services/selection.service';
import { AgenticEcommerceComponent } from './agentic-ecommerce/agentic-ecommerce.component';

@Component({
  selector: 'app-website-audit',
  templateUrl: './website-audit.component.html',
})
export class WebsiteAuditComponent implements OnInit {
  constructor(
    public router: Router,
    private route: ActivatedRoute,
    private sidenavService: SidenavService,
    private localStorageService: LocalStorageService,
    private pageTitle: PageTitleService,
    private websiteAuditService: WebsiteAuditService,
    private notificationService: NotificationService,
    private seoService: SeoService,
    private selectorService: SelectionService,
  ) {}

  private subs = new Subscription();

  public selectedTab = 0;
  public tabs = [
    {
      label: 'Overview',
      role: 'websiteAudit',
      component: OverviewComponent,
      disabled: false,
    },
    {
      label: 'UX',
      role: 'websiteAudit',
      component: UxComponent,
      disabled: false,
    },
    {
      label: 'CR',
      role: 'websiteAudit',
      component: CrComponent,
      disabled: false,
    },
    {
      label: 'SEO',
      role: 'websiteAudit',
      component: SeoComponent,
      disabled: true,
    },
    {
      label: 'GEO',
      role: 'websiteAudit',
      component: GeoComponent,
      disabled: true,
    },
    {
      label: 'Technical',
      role: 'websiteAudit',
      component: TechnicalComponent,
      disabled: false,
    },
    {
      label: 'Diagnostic',
      role: 'websiteAudit',
      component: DiagnosticComponent,
      disabled: false,
    },
    {
      label: 'Agentic Ecommerce',
      role: 'websiteAudit',
      component: AgenticEcommerceComponent,
      disabled: false,
    },
    {
      label: 'Audits',
      role: 'websiteAudit',
      component: ManagementComponent,
      disabled: false,
      dontShowOnShare: true,
    },
  ];

  taskId: string = '';
  selectedAuditTask: any = null;
  isShareableComponent: boolean = false;
  token: string = '';

  private selectedTaskResponse: any;

  private timeout: any;
  private timeoutSeconds: number = 20;

  private timeoutTasks: any;

  ngOnInit(): void {
    this.pageTitle.setTitle('Website Audit');

    if (this.router.url.includes('/share/website-audit')) {
      this.isShareableComponent = true;
    }

    const capitalizeFirstLetter = (str: any) => {
      try {
        return str.charAt(0).toUpperCase() + str.slice(1);
      } catch (e) {
        return str;
      }
    };

    this.subs.add(
      this.sidenavService.getActiveTabIndex().subscribe((index: any) => {
        const isDisabled = this.tabs[index].disabled;
        if (isDisabled) {
          this.selectedTab = this.tabs.findIndex((tab) => !tab.disabled);
        } else {
          this.selectedTab = index;
        }
      }),
    );

    const OverviewTab: any = this.tabs.find((t: any) => t.label == 'Overview');
    const UXTab: any = this.tabs.find((t: any) => t.label == 'UX');
    const CRTab: any = this.tabs.find((t: any) => t.label == 'CR');
    const TechTab: any = this.tabs.find((t: any) => t.label == 'Technical');
    const DiagTab: any = this.tabs.find((t: any) => t.label == 'Diagnostic');
    const SEOCard: any = this.tabs.find((t: any) => t.label == 'SEO');
    const GEOCard: any = this.tabs.find((t: any) => t.label == 'GEO');

    if (this.isShareableComponent) {
      this.tabs = this.tabs.filter((tab) => !tab.dontShowOnShare);

      this.token = this.route.snapshot.paramMap.get('token') as string;

      sessionStorage.setItem('shareable-website-audit-auth', this.token);

      const SEOCard: any =
        this.tabs.find((card: any) => card.label == 'SEO') || {};
      const GEOCard: any =
        this.tabs.find((card: any) => card.label == 'GEO') || {};

      this.subs.add(
        this.websiteAuditService.getAuditTaskFromToken(this.token).subscribe({
          next: (res: any) => {
            if (res) {
              res['domain'] = this.getDomainFromUrl(res?.url);
              res['formatted_domain'] = capitalizeFirstLetter(
                this.getDomainFromUrl(res?.url)?.split('.')[0],
              );

              if (typeof res?.target === 'string' && res.target) {
                sessionStorage.setItem('target', res.target);
              }

              // The shared-task response can omit `type` (like the single-task
              // detail), and the public page has no audit list to backfill from.
              // Recover it from the share link: an explicit `type`, or the
              // `crawl_`-prefixed taskId that Agentic Ecommerce audits always
              // carry — so a Crawler audit hides Overview and defaults to the
              // Agentic tab here too.
              if (!res.type) {
                const qp = new URLSearchParams(window.location.search);
                const sharedType = qp.get('type');
                const taskId =
                  qp.get('taskId') || res?.taskId || res?.parentTaskId || '';
                if (sharedType) {
                  res.type = sharedType;
                } else if (String(taskId).startsWith('crawl_')) {
                  res.type = 'Crawler';
                }
              }

              if (res?.type === 'Crawler') {
                // Keep the tab order consistent with the dashboard: disable
                // Overview + the full-audit tabs and default to Agentic Ecommerce
                // in its usual place (after Diagnostic), instead of hoisting it
                // to the front in Overview's slot.
                this.applyCrawlerTabs();
                this.websiteAuditService.updateSelectedAuditTask(res);
                return;
              }

              // New API exposes section status under overview.status.target;
              // fall back to the legacy flat progress fields for older payloads.
              const statusTarget = res?.overview?.status?.target;
              if (statusTarget) {
                UXTab['disabled'] = !this.isSectionDone(statusTarget.ux);
                CRTab['disabled'] = !this.isSectionDone(statusTarget.cr);
                TechTab['disabled'] = !this.isSectionDone(statusTarget.technical);
                DiagTab['disabled'] = !this.isSectionDone(
                  statusTarget.diagnostic,
                );
              } else {
                UXTab['disabled'] =
                  res?.uxProgress < 100 || res?.crStatus == 'failed';
                CRTab['disabled'] =
                  res?.uxProgress < 100 || res?.crStatus == 'failed';
                TechTab['disabled'] =
                  res?.insightProgress < 100 ||
                  res?.technicalStatus == 'failed';
                DiagTab['disabled'] =
                  res?.errorProgress < 100 || res?.errorStatus == 'failed';
              }

              this.subs.add(
                this.websiteAuditService
                  .getAuditGeneral({
                    domain: res['domain'],
                  })
                  .subscribe({
                    next: (data: any) => {
                      res['website_name'] = data?.general_obj?.website_name;
                      this.websiteAuditService.updateSelectedAuditTask(res);

                      SEOCard['disabled'] = false;
                      GEOCard['disabled'] = false;
                    },
                    error: (err: any) => {
                      this.websiteAuditService.updateSelectedAuditTask(res);
                      SEOCard['disabled'] = true;
                      GEOCard['disabled'] = true;
                    },
                  }),
              );
            }
          },
          error: (err: any) => {
            this.notificationService.error(err?.error?.message);
          },
        }),
      );
      return;
    }

    const params = new URLSearchParams(window.location.search);

    params.forEach((value, key) => {
      if (key === 'taskId') {
        this.taskId = value;
      }
    });

    // target change
    this.subs.add(
      this.selectorService.currentMessage.subscribe((message) => {
        this.selectedTaskResponse = null;
        this.selectedAuditTask = null;
        this.websiteAuditService.fetchAuditTasks();
      }),
    );

    // tasks list
    this.subs.add(
      this.websiteAuditService.getAuditTasks().subscribe((tasks) => {
        const isEveryTaskFailed = tasks?.every(
          (t: any) => t.status === 'Failed',
        );

        if (tasks?.length > 0 && !isEveryTaskFailed) {
          const areThereTasksInProgress = tasks.some(
            (task: any) => task.progress < 100,
          );

          if (this.timeoutTasks) {
            clearTimeout(this.timeoutTasks);
            this.timeoutTasks = null;
          }
          if (areThereTasksInProgress) {
            this.timeoutTasks = setTimeout(() => {
              this.websiteAuditService.fetchAuditTasks();
            }, this.timeoutSeconds * 1000);
          }

          if (
            !this.selectedTaskResponse ||
            this.selectedTaskResponse?.progress < 100 ||
            !this.sameAudit(this.selectedTaskResponse, this.selectedAuditTask)
          ) {
            if (this.taskId) {
              // The list keys audits by the parent/canonical taskId, but the
              // single-task detail returns the latest run's taskId. Match on
              // either (taskId or parentTaskId) so re-runs don't "lose" the
              // selected audit.
              this.selectedAuditTask =
                this.findAuditInList(tasks, [
                  this.taskId,
                  this.selectedTaskResponse?.taskId,
                  this.selectedTaskResponse?.parentTaskId,
                ]) || null;
            } else {
              let task = this.findTaskFromStatus(tasks, 'done');
              const firstTask = tasks?.[0] || null;

              // if done doesn't exists get one that is running
              if (!task) {
                task = this.findTaskFromStatus(tasks, 'running');
              }

              // get the first one if its not failed
              if (!task && firstTask?.status != 'Failed') {
                task = firstTask;
              }

              this.selectedAuditTask = task;
            }

            if (!this.selectedAuditTask) {
              this.selectedAuditTask =
                tasks?.find(
                  (task: any) => task?.status?.toLowerCase() === 'done',
                ) ||
                tasks?.[0] ||
                null;
            }

            // Configure tabs from the known list type right away, so an
            // Agentic Ecommerce audit never briefly shows Overview while its
            // detail (which also disables the tab) is still loading.
            if (this.selectedAuditTask?.type === 'Crawler') {
              this.applyCrawlerTabs();
            }

            this.router.navigate([], {
              queryParams: {
                taskId:
                  this.selectedTaskResponse?.parentTaskId ||
                  this.selectedTaskResponse?.taskId ||
                  this.selectedAuditTask?.parentTaskId ||
                  this.selectedAuditTask?.taskId,
              },
              queryParamsHandling: 'merge',
              replaceUrl: true,
            });

            this.getAuditTask();
          }
        } else {
          this.selectedAuditTask = null;
          this.tabs.forEach((tab: any) => {
            if (
              tab.label != 'Overview' &&
              tab.label != 'Audits' &&
              tab.label != 'Agentic Ecommerce'
            ) {
              tab.disabled = true;
            }
          });
          this.websiteAuditService.updateSelectedAuditTask(null);
        }
      }),
    );

    // selected task
    this.subs.add(
      this.websiteAuditService.getSelectedAuditTask().subscribe((res: any) => {
        if (res) {
          if (this.selectedTaskResponse?.taskId != res?.taskId) {
            setTimeout(() => {
              this.getAuditTask();
            }, 50);
          }
          this.selectedTaskResponse = res;
          this.taskId = res?.taskId || this.taskId;

          if (this.selectedTaskResponse?.type === 'Crawler') {
            this.applyCrawlerTabs();
            return;
          }
          OverviewTab['disabled'] = false;

          const statusTarget = res?.overview?.status?.target;
          if (statusTarget) {
            UXTab['disabled'] = !this.isSectionDone(statusTarget.ux);
            CRTab['disabled'] = !this.isSectionDone(statusTarget.cr);
            TechTab['disabled'] = !this.isSectionDone(statusTarget.technical);
            DiagTab['disabled'] = !this.isSectionDone(statusTarget.diagnostic);
          } else {
            UXTab['disabled'] =
              res?.uxProgress < 100 || res?.crStatus == 'failed';
            CRTab['disabled'] =
              res?.uxProgress < 100 || res?.crStatus == 'failed';
            TechTab['disabled'] =
              res?.insightProgress < 100 || res?.technicalStatus == 'failed';
            DiagTab['disabled'] =
              res?.errorProgress < 100 || res?.errorStatus == 'failed';
          }

          const crawlStatus = res?.organicProgress?.status;
          const averageStatusBar = res?.organicProgress?.progress;

          if (averageStatusBar >= 100 && crawlStatus != 'failed') {
            SEOCard['disabled'] = false;
            GEOCard['disabled'] = false;
          } else {
            SEOCard['disabled'] = true;
            GEOCard['disabled'] = true;
          }

          if (this.tabs[this.selectedTab].disabled) {
            this.selectedTab = 0;
            this.sidenavService.updateActiveTabIndex(this.selectedTab);
          }
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();

    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }

    if (this.timeoutTasks) {
      clearTimeout(this.timeoutTasks);
      this.timeoutTasks = null;
    }
  }

  private findTaskFromStatus(tasks: any, status: string) {
    return (
      tasks.find((task: any) => task?.status?.toLowerCase() === status) || null
    );
  }

  // A one-time/biweekly audit is identified by its parent taskId, while each
  // run has its own taskId. The list shows the parent (canonical) task and the
  // detail endpoint returns the latest run, so selection must reconcile both.
  private auditIds(t: any): string[] {
    if (!t) return [];
    return [t.taskId, t.parentTaskId].filter(Boolean);
  }

  private sameAudit(a: any, b: any): boolean {
    const idsA = this.auditIds(a);
    const idsB = this.auditIds(b);
    return (
      idsA.length > 0 && idsB.length > 0 && idsA.some((id) => idsB.includes(id))
    );
  }

  private findAuditInList(tasks: any[], ids: (string | undefined)[]): any {
    const wanted = (ids || []).filter(Boolean) as string[];
    if (!wanted.length) return null;
    return (
      (tasks || []).find((t: any) =>
        this.auditIds(t).some((id) => wanted.includes(id)),
      ) || null
    );
  }

  private isSectionDone(status: any): boolean {
    return (status || '').toString().toLowerCase() === 'done';
  }

  // Agentic Ecommerce (Crawler) audits have no full-audit report, so the only
  // relevant report tab is Agentic Ecommerce. Disable Overview + the full-audit
  // tabs (grayed, like UX/CR on an unfinished audit) and land on Agentic. Kept
  // in one place so it can run eagerly from the list type and again once the
  // detail resolves — no window where Overview is the active tab for a crawler.
  private applyCrawlerTabs(): void {
    ['Overview', 'UX', 'CR', 'SEO', 'GEO', 'Technical', 'Diagnostic'].forEach(
      (label) => {
        const tab = this.tabs.find((t: any) => t.label === label);
        if (tab) tab.disabled = true;
      },
    );

    const agenticIndex = this.tabs.findIndex(
      (t: any) => t.label === 'Agentic Ecommerce',
    );
    // Switch only when the current tab isn't usable for a crawler audit (i.e.
    // it just got disabled). Staying put on an enabled tab like Audits is fine.
    const current = this.tabs[this.selectedTab];
    if (agenticIndex > -1 && (!current || current.disabled)) {
      this.selectedTab = agenticIndex;
      this.sidenavService.updateActiveTabIndex(this.selectedTab);
    }
  }

  // The single-task endpoint now returns a nested shape:
  // { target, selectedCompetitor, availableCompetitors, overview, sections }
  // Flatten `target` to the top level (so taskId/url/status keep working) and
  // attach the new blocks for the redesigned Overview to consume.
  private normalizeAuditResponse(res: any): any {
    if (res && res.target && typeof res.target === 'object') {
      return {
        ...res.target,
        overview: res.overview || null,
        sections: res.sections || null,
        selectedCompetitor: res.selectedCompetitor || null,
        availableCompetitors: res.availableCompetitors || [],
      };
    }
    return res;
  }

  getAuditTask() {
    if (!this.selectedAuditTask) {
      return;
    }

    const taskId =
      this.selectedTaskResponse?.taskId || this.selectedAuditTask?.taskId;

    this.subs.add(
      this.websiteAuditService.getAuditTask(taskId).subscribe({
        next: (res) => {
          const normalized = this.normalizeAuditResponse(res);

          this.websiteAuditService.updateSelectedAuditTask({
            ...this.selectedTaskResponse,
            ...normalized,
          });

          if (
            !this.selectedTaskResponse?.organicProgress ||
            this.selectedTaskResponse?.organicProgress?.progress < 100
          ) {
            this.getStatusOfProject(normalized);
          }
        },
        error: (err) => {
          this.websiteAuditService.updateSelectedAuditTask(null);
          this.notificationService.error(err?.error?.message);
        },
      }),
    );
  }

  getDomainFromUrl(url: string): string | null {
    try {
      const normalized = url.startsWith('http') ? url : `https://${url}`;

      const { hostname } = new URL(normalized);
      return hostname.replace(/^www\./, '');
    } catch {
      return null;
    }
  }

  getStatusOfProject(res: any) {
    if (!res) return;

    const domainFromUrl = this.getDomainFromUrl(res?.url);

    this.seoService.getStatusOfProject({ domain: domainFromUrl }).subscribe({
      next: (res: any) => {
        const crawlStatus = res?.progresses?.[0]?.crawl_status || '';

        this.websiteAuditService.updateSelectedAuditTask({
          ...this.selectedTaskResponse,
          organicProgress: {
            progress: res?.averageStatusBar,
            status: crawlStatus,
          },
        });
      },
      error: (err) => {
        this.websiteAuditService.updateSelectedAuditTask({
          ...this.selectedTaskResponse,
          organicProgress: {
            progress: 0,
            status: 'failed',
          },
        });
      },
    });
  }

  private _animationRunning = false;
  get animationRunning() {
    return this._animationRunning;
  }
  indexChange() {
    this._animationRunning = true;
  }
  onAnimationDone() {
    this.sidenavService.updateActiveTabIndex(this.selectedTab);
    this._animationRunning = false;
    this.sidenavService.triggerPageChange();
  }

  checkRoleGuard(page: any) {
    const role: string[] = this.localStorageService.get('privileges') || [];
    if (role.length > 0) {
      if (role.findIndex((priv) => priv == page) < 0) {
        return false;
      } else {
        return true;
      }
    } else {
      this.router.navigate(['login']);
      return false;
    }
  }
}
